import { useState, useEffect, lazy, Suspense } from "react";
import { useGameStore } from "./hooks/useGameStore";
import { GameScreen } from "./components/GameScreen";
import { TemplateSelector } from "./components/TemplateSelector";
import { useAuthStore } from "./stores/authStore";
import { useHistoryStore } from "./stores/historyStore";
import { useMultiplayerStore } from "./stores/multiplayerStore";
import { useTranslation } from "./stores/settingsStore";
import { useTheme } from "./hooks/useTheme";

// Lazy load less frequently used pages
const StatsPage = lazy(() => import("./components/StatsPage").then(m => ({ default: m.StatsPage })));
const SettingsPage = lazy(() => import("./components/SettingsPage").then(m => ({ default: m.SettingsPage })));
const AuthPage = lazy(() => import("./components/AuthPage").then(m => ({ default: m.AuthPage })));
const LobbyPage = lazy(() => import("./components/LobbyPage").then(m => ({ default: m.LobbyPage })));
const WaitingRoom = lazy(() => import("./components/WaitingRoom").then(m => ({ default: m.WaitingRoom })));
const MultiplayerGame = lazy(() => import("./components/MultiplayerGame").then(m => ({ default: m.MultiplayerGame })));
const LeaderboardPage = lazy(() => import("./components/LeaderboardPage").then(m => ({ default: m.LeaderboardPage })));

// Loading fallback
function PageLoader() {
  return (
    <div className="page-loader" aria-live="polite" aria-busy="true">
      <div className="page-loader__spinner" />
      <span>Loading...</span>
    </div>
  );
}

type Page = "home" | "game" | "stats" | "settings" | "auth" | "lobby" | "waiting" | "multiplayer" | "leaderboard";

const PLAYER_ID = "player1";

export function App() {
  const t = useTranslation();
  const [currentPage, setCurrentPage] = useState<Page>("home");

  // Initialize theme system (applies dark/light class to html element)
  useTheme();

  const { gameState, startGame, roll, toggleHold, announce, score, resetGame } =
    useGameStore();

  const { user, isAuthenticated, checkAuth } = useAuthStore();
  const { saveLocalGame } = useHistoryStore();
  const { room: _room, game, reset: resetMultiplayer } = useMultiplayerStore();

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Save game state when it changes
  useEffect(() => {
    if (gameState) {
      const status = gameState.status === "finished" ? "completed" : "in_progress";
      saveLocalGame(gameState, status);
    }
  }, [gameState, saveLocalGame]);

  // Auto-navigate based on multiplayer state
  useEffect(() => {
    if (currentPage === "waiting" && game) {
      setCurrentPage("multiplayer");
    }
  }, [game, currentPage]);

  const handleStartGame = (templateId: string) => {
    startGame(templateId, [PLAYER_ID]);
    setCurrentPage("game");
  };

  const handleQuitGame = () => {
    if (gameState) {
      saveLocalGame(gameState, "abandoned");
    }
    resetGame();
    setCurrentPage("home");
  };

  const handleGameComplete = () => {
    if (gameState) {
      saveLocalGame(gameState, "completed");
    }
    resetGame();
    setCurrentPage("home");
  };

  const handleMultiplayerBack = () => {
    resetMultiplayer();
    setCurrentPage("home");
  };

  // Render page based on current route
  if (currentPage === "stats") {
    return (
      <Suspense fallback={<PageLoader />}>
        <StatsPage onBack={() => setCurrentPage("home")} />
      </Suspense>
    );
  }

  if (currentPage === "settings") {
    return (
      <Suspense fallback={<PageLoader />}>
        <SettingsPage onBack={() => setCurrentPage("home")} />
      </Suspense>
    );
  }

  if (currentPage === "auth") {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthPage
          onBack={() => setCurrentPage("home")}
          onSuccess={() => setCurrentPage("home")}
        />
      </Suspense>
    );
  }

  if (currentPage === "leaderboard") {
    return (
      <Suspense fallback={<PageLoader />}>
        <LeaderboardPage onBack={() => setCurrentPage("home")} />
      </Suspense>
    );
  }

  if (currentPage === "lobby") {
    return (
      <Suspense fallback={<PageLoader />}>
        <LobbyPage
          onBack={() => setCurrentPage("home")}
          onRoomJoined={() => setCurrentPage("waiting")}
        />
      </Suspense>
    );
  }

  if (currentPage === "waiting") {
    return (
      <Suspense fallback={<PageLoader />}>
        <WaitingRoom
          onBack={handleMultiplayerBack}
          onGameStarted={() => setCurrentPage("multiplayer")}
        />
      </Suspense>
    );
  }

  if (currentPage === "multiplayer") {
    return (
      <Suspense fallback={<PageLoader />}>
        <MultiplayerGame
          onBack={handleMultiplayerBack}
          onGameEnd={handleMultiplayerBack}
        />
      </Suspense>
    );
  }

  // Game screen (when a solo game is in progress)
  if (currentPage === "game" && gameState) {
    return (
      <div className="app">
        <header className="app__header">
          <h1 className="app__title">YambPro</h1>
          <button
            type="button"
            className="app__quit-btn"
            onClick={handleQuitGame}
          >
            {t("quitGame")}
          </button>
        </header>
        <main className="app__main">
          <GameScreen
            gameState={gameState}
            playerId={PLAYER_ID}
            onRoll={() => roll(PLAYER_ID)}
            onToggleHold={(index) => toggleHold(PLAYER_ID, index)}
            onAnnounce={(category) => announce(PLAYER_ID, category)}
            onScore={(columnId, category) => score(PLAYER_ID, columnId, category)}
            onReset={handleGameComplete}
          />
        </main>
      </div>
    );
  }

  // Home screen
  return (
    <div className="app">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <header className="app__header">
        <h1 className="app__title">YambPro</h1>
        <nav className="app__nav">
          {isAuthenticated ? (
            <button
              type="button"
              className="nav-btn user-btn"
              onClick={() => setCurrentPage("settings")}
            >
              {user?.username?.charAt(0).toUpperCase() ?? "U"}
            </button>
          ) : (
            <button
              type="button"
              className="nav-btn"
              onClick={() => setCurrentPage("auth")}
            >
              {t("login")}
            </button>
          )}
        </nav>
      </header>
      <nav aria-label="Yamb resources" style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "1rem", padding: "1rem" }}>
        <a href="/">Home</a>
        <a href="/how-to-play-yamb/">How to play</a>
        <a href="/yamb-score-sheet/">Printable score sheet</a>
      </nav>
      <main id="main-content" className="app__main" role="main">
        <TemplateSelector onSelectTemplate={handleStartGame} />

        <div className="home-actions">
          <button
            type="button"
            className="home-action-btn multiplayer-btn"
            onClick={() => setCurrentPage("lobby")}
          >
            <span className="action-icon">👥</span>
            <span className="action-label">Multiplayer</span>
          </button>
          <button
            type="button"
            className="home-action-btn leaderboard-btn"
            onClick={() => setCurrentPage("leaderboard")}
          >
            <span className="action-icon">🏆</span>
            <span className="action-label">Leaderboards</span>
          </button>
          <button
            type="button"
            className="home-action-btn"
            onClick={() => setCurrentPage("stats")}
          >
            <span className="action-icon">📊</span>
            <span className="action-label">{t("stats")}</span>
          </button>
          <button
            type="button"
            className="home-action-btn"
            onClick={() => setCurrentPage("settings")}
          >
            <span className="action-icon">⚙️</span>
            <span className="action-label">{t("settings")}</span>
          </button>
        </div>
      </main>
    </div>
  );
}
